import { ADMIN_DATE_PLACEHOLDER, classifyAdminCallableError } from './adminUserDisplay.js';

export { ADMIN_DATE_PLACEHOLDER, classifyAdminCallableError };

/** Safe document validation reason codes from backend HttpsError.details.reason */
export const LETTER_TEMPLATE_DOCUMENT_ERROR_REASONS = Object.freeze([
  'invalid_document',
  'unsupported_node',
  'unsupported_mark',
  'invalid_alignment',
  'invalid_placeholder',
  'invalid_qr_layout',
  'invalid_font',
  'invalid_image',
  'invalid_image_asset',
  'rich_payload_required',
  'bilingual_payload_required',
  'translation_source_mismatch',
]);

/** Safe PDF preview reason codes from backend HttpsError.details.reason */
export const LETTER_TEMPLATE_PDF_PREVIEW_ERROR_REASONS = Object.freeze([
  'forbidden_preview_field',
  'letter_template_asset_missing',
  'letter_template_asset_invalid',
  'letter_template_overflow',
  'letter_template_preview_failed',
  'letter_template_preview_too_large',
]);

/**
 * @param {unknown} error
 * @returns {'template_version_conflict' | 'template_archived' | 'template_version_exists' | null}
 */
export function extractLetterTemplateBusinessError(error) {
  const msg = String(error?.message || '').trim();
  if (msg === 'template_version_conflict') return 'template_version_conflict';
  if (msg === 'template_archived') return 'template_archived';
  if (msg === 'template_version_exists') return 'template_version_exists';
  return null;
}

/**
 * Extract safe document reason from callable error details. Never returns raw messages.
 * @param {unknown} error
 * @returns {string | null}
 */
export function extractLetterTemplateDocumentErrorReason(error) {
  const reason = error?.details?.reason;
  if (typeof reason !== 'string') return null;
  return LETTER_TEMPLATE_DOCUMENT_ERROR_REASONS.includes(reason) ? reason : null;
}

/**
 * @param {unknown} error
 * @returns {string | null}
 */
export function extractLetterTemplatePdfPreviewErrorReason(error) {
  const reason = error?.details?.reason;
  if (typeof reason !== 'string') return null;
  return LETTER_TEMPLATE_PDF_PREVIEW_ERROR_REASONS.includes(reason) ? reason : null;
}

/**
 * @param {string | null | undefined} kind
 */
export function adminProspectLetterTemplateErrorKey(kind) {
  if (kind === 'unauthenticated') return 'admin.prospection.letterTemplate.error.unauthenticated';
  if (kind === 'permission-denied') return 'admin.prospection.letterTemplate.error.permissionDenied';
  if (kind === 'not-found') return 'admin.prospection.letterTemplate.error.notFound';
  if (kind === 'unavailable') return 'admin.prospection.letterTemplate.error.unavailable';
  if (kind === 'failed-precondition') return 'admin.prospection.letterTemplate.error.failedPrecondition';
  if (kind === 'invalid-argument') return 'admin.prospection.letterTemplate.error.invalidArgument';
  if (kind === 'internal') return 'admin.prospection.letterTemplate.error.internal';
  return 'admin.prospection.letterTemplate.error.generic';
}

/**
 * @param {unknown} error
 */
export function adminProspectLetterTemplateErrorKeyFromError(error) {
  const business = extractLetterTemplateBusinessError(error);
  if (business === 'template_version_conflict') {
    return 'admin.prospection.letterTemplate.error.versionConflict';
  }
  if (business === 'template_archived') {
    return 'admin.prospection.letterTemplate.error.archived';
  }
  if (
    error?.code === 'letter_template_payload_not_serializable' ||
    error?.code === 'letter_template_payload_not_callable_safe' ||
    error?.message === 'letter_template_payload_not_serializable' ||
    error?.message === 'letter_template_payload_not_callable_safe'
  ) {
    return 'admin.prospection.letterTemplate.error.invalidDocument';
  }
  const documentReason = extractLetterTemplateDocumentErrorReason(error);
  if (documentReason === 'unsupported_node') {
    return 'admin.prospection.letterTemplate.error.unsupportedNode';
  }
  if (documentReason === 'unsupported_mark') {
    return 'admin.prospection.letterTemplate.error.unsupportedMark';
  }
  if (documentReason === 'invalid_alignment') {
    return 'admin.prospection.letterTemplate.error.invalidAlignment';
  }
  if (documentReason === 'invalid_placeholder') {
    return 'admin.prospection.letterTemplate.error.invalidPlaceholder';
  }
  if (documentReason === 'invalid_qr_layout') {
    return 'admin.prospection.letterTemplate.error.invalidQrLayout';
  }
  if (documentReason === 'invalid_font') {
    return 'admin.prospection.letterTemplate.error.invalidFont';
  }
  if (documentReason === 'invalid_image' || documentReason === 'invalid_image_asset') {
    return 'admin.prospection.letterTemplate.error.invalidImage';
  }
  if (documentReason === 'rich_payload_required') {
    return 'admin.prospection.letterTemplate.error.richPayloadRequired';
  }
  if (documentReason === 'bilingual_payload_required') {
    return 'admin.prospection.letterTemplate.error.bilingualPayloadRequired';
  }
  if (documentReason === 'translation_source_mismatch') {
    return 'admin.prospection.letterTemplate.error.translationSourceMismatch';
  }
  if (documentReason === 'invalid_document') {
    return 'admin.prospection.letterTemplate.error.invalidDocument';
  }
  return adminProspectLetterTemplateErrorKey(classifyAdminCallableError(error));
}

/**
 * @param {unknown} error
 */
export function adminProspectLetterTemplatePdfPreviewErrorKeyFromError(error) {
  if (
    error?.code === 'letter_template_payload_not_serializable' ||
    error?.code === 'letter_template_payload_not_callable_safe' ||
    error?.message === 'letter_template_payload_not_serializable' ||
    error?.message === 'letter_template_payload_not_callable_safe'
  ) {
    return 'admin.prospection.letterTemplate.error.invalidDocument';
  }
  const documentReason = extractLetterTemplateDocumentErrorReason(error);
  if (documentReason) {
    return adminProspectLetterTemplateErrorKeyFromError(error);
  }
  const previewReason = extractLetterTemplatePdfPreviewErrorReason(error);
  if (previewReason === 'letter_template_overflow') {
    return 'admin.prospection.letterTemplate.pdfPreview.error.overflow';
  }
  if (previewReason === 'letter_template_preview_too_large') {
    return 'admin.prospection.letterTemplate.pdfPreview.error.tooLarge';
  }
  if (
    previewReason === 'letter_template_asset_missing' ||
    previewReason === 'letter_template_asset_invalid'
  ) {
    return 'admin.prospection.letterTemplate.pdfPreview.error.asset';
  }
  if (previewReason === 'letter_template_preview_failed') {
    return 'admin.prospection.letterTemplate.pdfPreview.error.failed';
  }
  if (previewReason === 'forbidden_preview_field') {
    return 'admin.prospection.letterTemplate.pdfPreview.error.forbiddenField';
  }
  return adminProspectLetterTemplateErrorKey(classifyAdminCallableError(error));
}

/**
 * @param {string | null | undefined} status
 * @param {(key: string) => string} t
 */
export function adminProspectLetterTemplateStatusLabel(status, t) {
  if (status === 'archived') return t('admin.prospection.letterTemplate.status.archived');
  return t('admin.prospection.letterTemplate.status.active');
}

/**
 * @param {string | null | undefined} status
 */
export function adminProspectLetterTemplateStatusBadgeClass(status) {
  if (status === 'archived') return 'bg-slate-100 text-slate-800 ring-slate-200';
  return 'bg-emerald-100 text-emerald-900 ring-emerald-200';
}

export const LETTER_TEMPLATE_PLACEHOLDER_KEYS = Object.freeze([
  'OWNER_NAME',
  'OWNER_MAILING_ADDRESS',
  'OWNER_CITY',
  'OWNER_PROVINCE',
  'OWNER_POSTAL_CODE',
  'PROPERTY_ADDRESS',
  'PROPERTY_CITY',
  'PROPERTY_PROVINCE',
  'PROPERTY_POSTAL_CODE',
  'NUMBER_OF_UNITS',
]);

export const LETTER_TEMPLATE_PLACEHOLDER_OWNER_KEYS = Object.freeze([
  'OWNER_NAME',
  'OWNER_MAILING_ADDRESS',
  'OWNER_CITY',
  'OWNER_PROVINCE',
  'OWNER_POSTAL_CODE',
]);

export const LETTER_TEMPLATE_PLACEHOLDER_PROPERTY_KEYS = Object.freeze([
  'PROPERTY_ADDRESS',
  'PROPERTY_CITY',
  'PROPERTY_PROVINCE',
  'PROPERTY_POSTAL_CODE',
  'NUMBER_OF_UNITS',
]);

/**
 * @param {string} key
 * @param {(key: string) => string} t
 */
export function letterTemplatePlaceholderLabel(key, t) {
  return t(`admin.prospection.letterTemplate.placeholder.${key}`);
}

export const LETTER_TEMPLATE_PREVIEW_SAMPLES = Object.freeze({
  OWNER_NAME: 'Jean Tremblay',
  OWNER_MAILING_ADDRESS: '456 avenue des Érables',
  OWNER_CITY: 'Longueuil',
  OWNER_PROVINCE: 'QC',
  OWNER_POSTAL_CODE: 'J4K 2A2',
  PROPERTY_ADDRESS: '123 rue Principale',
  PROPERTY_CITY: 'Longueuil',
  PROPERTY_PROVINCE: 'QC',
  PROPERTY_POSTAL_CODE: 'J4K 1A1',
  NUMBER_OF_UNITS: '6',
});
